import React, { useEffect, useRef, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { baseURL } from "../../js/helpers/baseURL";
import { clearUserBootstrap } from "../../js/helpers/userBootstrap";
import { DashboardLink } from "./DashboardOpenOverlay";
import Icon from "./Icon";

const avatarSrc = (user) => (user?.profile_image ? user.profile_image : "/images/avatar.png");

const useLogout = (onDone) => {
    const history = useHistory();

    return useCallback(() => {
        confirmAlert({
            title: "Are you sure you want to logout?",
            buttons: [
                {
                    label: "Yes",
                    onClick: () => {
                        axios
                            .post(`${baseURL}logout`, {}, {
                                headers: { Authorization: `Bearer ${Cookies.get("access_token")}` },
                            })
                            .catch((error) => console.error(error))
                            .finally(() => {
                                localStorage.clear();
                                clearUserBootstrap();
                                Cookies.remove("access_token");
                                Cookies.remove("mydev_token");
                                onDone?.();
                                history.push("/");
                            });
                    },
                },
                { label: "No" },
            ],
            overlayClassName: "confirm-overlay",
        });
    }, [history, onDone]);
};

const MenuLinks = ({ user, onNavigate, onLogout, className }) => (
    <div className={className}>
        <a href="/profile" className="clk-user-menu-item" onClick={onNavigate}>
            <Icon name="user" size={18} />
            <span>My account</span>
        </a>
        {user?.isAdmin && (
            <a href="/subscription/plan" className="clk-user-menu-item" onClick={onNavigate}>
                <Icon name="card" size={18} />
                <span>Subscription</span>
            </a>
        )}
        <button
            type="button"
            className="clk-user-menu-item clk-user-menu-item--danger"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onLogout}
        >
            <Icon name="log-out" size={18} />
            <span>Sign out</span>
        </button>
    </div>
);

export const LandingNavAuthed = ({ user }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const logout = useLogout(() => setOpen(false));

    useEffect(() => {
        const onPointerDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, []);

    const close = () => setOpen(false);

    return (
        <div className="clk-nav-authed" ref={rootRef}>
            <DashboardLink className="clk-btn clk-btn-primary clk-btn-sm">
                Dashboard <Icon name="arrow" size={14} className="clk-arrow" />
            </DashboardLink>
            <div className={`clk-user-menu${open ? " is-open" : ""}`}>
                <button
                    type="button"
                    className="clk-user-trigger"
                    aria-expanded={open}
                    aria-haspopup="menu"
                    onClick={() => setOpen((v) => !v)}
                >
                    <img src={avatarSrc(user)} alt="" className="clk-user-avatar" />
                    <span className="clk-user-name">{user?.name || "Account"}</span>
                    <Icon name="chevron-down" size={14} className="clk-user-chevron" />
                </button>
                <div className="clk-user-dropdown" role="menu">
                    <div className="clk-user-dropdown-head">
                        <img src={avatarSrc(user)} alt="" className="clk-user-dropdown-avatar" />
                        <div>
                            <strong>{user?.name || "Account"}</strong>
                            <span>{user?.email}</span>
                        </div>
                    </div>
                    <MenuLinks user={user} onNavigate={close} onLogout={logout} className="clk-user-dropdown-body" />
                </div>
            </div>
        </div>
    );
};

export const LandingMobileAuthed = ({ user, onClose }) => {
    const [cardOpen, setCardOpen] = useState(false);
    const logout = useLogout(onClose);

    const navigate = () => {
        onClose();
        setCardOpen(false);
    };

    return (
        <div className="clk-mobile-authed">
            <div className={`clk-mobile-user-card${cardOpen ? " is-open" : ""}`}>
                <button
                    type="button"
                    className="clk-mobile-user-card-trigger"
                    aria-expanded={cardOpen}
                    onClick={() => setCardOpen((v) => !v)}
                >
                    <img src={avatarSrc(user)} alt="" className="clk-mobile-user-avatar" />
                    <div className="clk-mobile-user-card-text">
                        <strong>{user?.name || "Account"}</strong>
                        <span>{user?.email}</span>
                    </div>
                    <Icon name="chevron-down" size={18} className="clk-mobile-user-chevron" />
                </button>
                <div className="clk-mobile-user-card-menu">
                    <a href="/profile" className="clk-user-menu-item" onClick={navigate}>
                        <Icon name="user" size={20} />
                        <span>My account</span>
                    </a>
                    {user?.isAdmin && (
                        <a href="/subscription/plan" className="clk-user-menu-item" onClick={navigate}>
                            <Icon name="card" size={20} />
                            <span>Subscription</span>
                        </a>
                    )}
                </div>
            </div>
            <div className="clk-mobile-actions">
                <DashboardLink
                    className="clk-btn clk-btn-primary"
                    onBeforeOpen={onClose}
                >
                    Dashboard <Icon name="arrow" size={16} className="clk-arrow" />
                </DashboardLink>
            </div>
            <div className="clk-mobile-user-links">
                <button
                    type="button"
                    className="clk-user-menu-item clk-user-menu-item--danger"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={logout}
                >
                    <Icon name="log-out" size={20} />
                    <span>Sign out</span>
                </button>
            </div>
        </div>
    );
};
